import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Mail, CheckCircle2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { payslipPDFBlob } from "../lib/payslipPdf";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const BulkPayslipEmailDialog = ({ open, onOpenChange, rows, fields, month, companyName }) => {
  const [phase, setPhase] = useState("idle");
  const [prog, setProg] = useState({ done: 0, queued: 0, noEmail: 0, failed: 0, already: 0 });
  const [batch, setBatch] = useState("");
  const [status, setStatus] = useState(null);
  const stop = useRef(false);
  const list = rows.filter((r) => r.employee_id && !r.draft);
  const drafts = rows.filter((r) => r.employee_id && r.draft).length;

  useEffect(() => { if (!open) { stop.current = true; setPhase("idle"); setStatus(null); } }, [open]);
  useEffect(() => {
    if (!batch || phase !== "done") return undefined;
    const tick = () => axios.get(`${API}/email-queue/status`, { params: { batch_id: batch } }).then((r) => setStatus(r.data)).catch(() => {});
    tick();
    const iv = setInterval(tick, 15000);
    return () => clearInterval(iv);
  }, [batch, phase]);

  const run = async () => {
    stop.current = false;
    const b = `pay-${month}-${Date.now()}`;
    setBatch(b); setPhase("running");
    const p = { done: 0, queued: 0, noEmail: 0, failed: 0, already: 0 };
    for (const row of list) {
      if (stop.current) break;
      try {
        const blob = await payslipPDFBlob({ ...row, month: row.month || month }, row.fields || fields, companyName);
        const fd = new FormData();
        fd.append("file", blob, `كشف-راتب-${month}.pdf`);
        fd.append("title", `كشف راتب شهر ${row.month || month}`);
        fd.append("ref_id", `payslip-${row.month || month}`);
        fd.append("queue", "true");
        fd.append("batch_id", b);
        const r = await axios.post(`${API}/employees/${row.employee_id}/email-doc`, fd).then((x) => x.data);
        if (r.duplicate) p.already += 1; else p.queued += 1;
      } catch (e) {
        if (e?.response?.status === 400 && String(e.response.data?.detail || "").includes("بريد")) p.noEmail += 1; else p.failed += 1;
      }
      p.done += 1;
      setProg({ ...p });
    }
    setPhase("done");
    if (p.queued) toast.success(`تم تجهيز ${p.queued} كشف — الإرسال يتم تلقائياً بالتدريج`);
    else toast.info("ما فيه كشوف جديدة للإرسال");
  };

  const pct = list.length ? Math.round((prog.done / list.length) * 100) : 0;
  return (
    <Dialog open={open} onOpenChange={(o) => phase !== "running" && onOpenChange(o)}>
      <DialogContent dir="rtl" className="w-[95vw] max-w-md font-body" data-testid="bulk-payslip-email-dialog">
        <DialogHeader className="text-start">
          <DialogTitle className="flex items-center gap-2 ps-8"><Mail className="h-5 w-5 text-blue-600" /> إرسال كشوف رواتب {month} بالإيميل</DialogTitle>
          <DialogDescription className="text-start">كل موظف يوصله كشف راتبه لحاله على بريده المسجّل. الإرسال يتم بالتدريج (رسالتين تقريباً كل دقيقة) عشان ما ينحجب البريد.</DialogDescription>
        </DialogHeader>
        {phase === "idle" && (<>
          <p className="text-sm text-slate-700">كشوف معتمدة جاهزة للإرسال: <b data-testid="bulk-payslip-count">{list.length}</b></p>
          {drafts > 0 && <p className="rounded-lg bg-amber-50 p-2 text-xs text-amber-800" data-testid="bulk-payslip-drafts">{drafts} كشف بانتظار الاعتماد — ما راح تنرسل إلا بعد اعتمادها.</p>}
          <p className="text-xs text-slate-500">الموظف اللي سبق وصله كشف نفس الشهر ما يتكرر له الإرسال.</p>
          <Button onClick={run} disabled={!list.length} className="w-full gap-2 bg-blue-600 hover:bg-blue-700" data-testid="bulk-payslip-start"><Mail className="h-4 w-4" /> ابدأ التجهيز والإرسال</Button>
        </>)}
        {phase !== "idle" && (
          <div className="space-y-3" data-testid="bulk-payslip-progress">
            <div className="h-2 overflow-hidden rounded-full bg-slate-100"><div className="h-full bg-blue-600 transition-[width]" style={{ width: `${pct}%` }} /></div>
            <p className="text-sm text-slate-700">{phase === "running" ? <><Loader2 className="inline h-4 w-4 animate-spin" /> جارٍ تجهيز الكشوف… {prog.done}/{list.length}</> : <><CheckCircle2 className="inline h-4 w-4 text-emerald-600" /> انتهى التجهيز</>}</p>
            <div className="grid grid-cols-4 gap-2 text-center text-xs">
              <div className="rounded-lg bg-emerald-50 p-2"><b className="block text-base text-emerald-700">{prog.queued}</b>جاهز للإرسال</div>
              <div className="rounded-lg bg-amber-50 p-2"><b className="block text-base text-amber-700" data-testid="bulk-payslip-noemail">{prog.noEmail}</b>بدون بريد</div>
              <div className="rounded-lg bg-slate-50 p-2"><b className="block text-base text-slate-700" data-testid="bulk-payslip-already">{prog.already}</b>مرسل سابقاً</div>
              <div className="rounded-lg bg-rose-50 p-2"><b className="block text-base text-rose-700">{prog.failed}</b>تعذّر</div>
            </div>
            {status && status.total > 0 && <p className="rounded-lg bg-blue-50 p-2 text-xs text-blue-800" data-testid="bulk-payslip-status">وصل فعلياً: <b>{status.sent}</b> من {status.total} · في الانتظار: {status.pending + status.sending}{status.failed ? ` · فشل: ${status.failed}` : ""} — تقدر تسكّر النافذة، الإرسال يكمل لحاله.</p>}
            {phase === "done" && <Button variant="outline" className="w-full" onClick={() => onOpenChange(false)} data-testid="bulk-payslip-close">إغلاق</Button>}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
